import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import { Building2, Globe, Phone, Mail, MapPin, Instagram, Facebook, MessageCircle, ArrowLeft, MapPinned } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import defaultBusiness from "@/assets/default-business.png";

type Business = {
  id: string;
  name_ar: string;
  name_en: string | null;
  activity: string | null;
  location: string | null;
  country: string | null;
  emirate: string | null;
  area: string | null;
  street: string | null;
  logo_url: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  instagram: string | null;
  facebook: string | null;
  twitter_x: string | null;
  whatsapp: string | null;
};

const SocialIcon = ({ type, url }: { type: string; url: string | null }) => {
  if (!url) return null;
  const config: Record<string, { icon: typeof Globe; color: string; href: string }> = {
    website: { icon: MapPinned, color: "hover:text-red-500", href: url },
    phone: { icon: Phone, color: "hover:text-green-400", href: `tel:${url}` },
    email: { icon: Mail, color: "hover:text-red-400", href: `mailto:${url}` },
    whatsapp: { icon: MessageCircle, color: "hover:text-green-500", href: `https://wa.me/${url.replace(/[^0-9]/g, "")}` },
    instagram: { icon: Instagram, color: "hover:text-pink-400", href: url.startsWith("http") ? url : `https://instagram.com/${url.replace("@", "")}` },
    facebook: { icon: Facebook, color: "hover:text-blue-500", href: url },
  };
  const c = config[type];
  if (!c) return null;
  return (
    <a href={c.href} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
      className={`h-8 w-8 rounded-full bg-secondary/50 flex items-center justify-center text-muted-foreground ${c.color} transition-all hover:bg-secondary`}>
      <c.icon className="h-3.5 w-3.5" />
    </a>
  );
};

const BusinessDetail = ({ biz, owners, open, onClose }: { biz: Business | null; owners: { id: string; name: string; photo_url: string | null }[]; open: boolean; onClose: () => void }) => {
  if (!biz) return null;
  const activities = biz.activity ? biz.activity.split(" | ").filter(a => a.trim()) : [];
  const contacts = [
    { icon: MapPinned, label: "الموقع على الخريطة", value: biz.website, href: biz.website, external: true },
    { icon: Phone, label: "رقم التواصل", value: biz.phone, href: `tel:${biz.phone}` },
    { icon: Mail, label: "البريد الإلكتروني", value: biz.email, href: `mailto:${biz.email}` },
    { icon: MessageCircle, label: "واتساب", value: biz.whatsapp, href: `https://wa.me/${biz.whatsapp?.replace(/[^0-9]/g, "")}`, external: true },
    { icon: Instagram, label: "انستغرام", value: biz.instagram, href: biz.instagram?.startsWith("http") ? biz.instagram : `https://instagram.com/${biz.instagram?.replace("@", "")}`, external: true },
    { icon: Facebook, label: "فيس بوك", value: biz.facebook, href: biz.facebook, external: true },
  ].filter(c => c.value);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="glass-card border-primary/20 max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl" dir="rtl">
        <DialogHeader>
          <DialogTitle className="font-amiri text-xl gold-text text-center">تفاصيل الشركة</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-5 pt-2">
          <div className="w-32 h-32 rounded-full overflow-hidden bg-secondary/30 flex items-center justify-center border-2 border-primary/30 shadow-lg ring-2 ring-primary/10 ring-offset-2 ring-offset-background">
            <img src={biz.logo_url || defaultBusiness} alt={biz.name_ar} className={`w-full h-full ${biz.logo_url ? 'object-cover' : 'object-contain p-3'}`} />
          </div>
          <div className="text-center">
            <h2 className="font-amiri text-2xl font-bold text-primary">{biz.name_ar}</h2>
            {biz.name_en && <p className="text-muted-foreground mt-1">{biz.name_en}</p>}
          </div>
          {activities.length > 0 && (
            <div className="flex flex-wrap gap-2 justify-center">
              {activities.map((a, i) => (
                <Badge key={i} variant="secondary" className="text-xs">{a}</Badge>
              ))}
            </div>
          )}
          {/* Address */}
          {(biz.country || biz.emirate || biz.area || biz.street) && (
            <div className="flex items-start gap-2 text-muted-foreground">
              <MapPin className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span className="text-sm">{[biz.country, biz.emirate, biz.area, biz.street].filter(Boolean).join(" - ")}</span>
            </div>
          )}
          {/* Map link */}
          {biz.location && biz.location.startsWith("http") && (
            <a href={biz.location} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-primary hover:underline">
              <MapPinned className="h-4 w-4" />
              <span className="text-sm">عرض الموقع على الخريطة</span>
            </a>
          )}
          {owners.length > 0 && (
            <div className="w-full pt-2 border-t border-primary/10">
              <p className="text-xs text-muted-foreground text-center mb-3">أصحاب الشركة</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {owners.map(o => (
                  <Badge key={o.id} variant="outline" className="text-xs border-primary/20 px-3 py-1.5 gap-1.5">
                    <Building2 className="h-3 w-3" />
                    {o.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          {contacts.length > 0 && (
            <div className="w-full pt-2 border-t border-primary/10">
              <p className="text-xs text-muted-foreground text-center mb-3">وسائل التواصل</p>
              <div className="flex flex-wrap gap-3 justify-center">
                {contacts.map((c, i) => (
                  <a key={i} href={c.href!} target={c.external ? "_blank" : undefined} rel={c.external ? "noopener noreferrer" : undefined}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-secondary/30 hover:bg-secondary/50 transition-all group min-w-[70px]">
                    <div className="h-10 w-10 rounded-full bg-primary/10 group-hover:bg-primary/20 flex items-center justify-center transition-all">
                      <c.icon className="h-5 w-5 text-primary" />
                    </div>
                    <span className="text-xs text-primary font-medium">اضغط هنا</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Businesses = () => {
  const [selected, setSelected] = useState<Business | null>(null);
  const [selectedOwners, setSelectedOwners] = useState<{ id: string; name: string; photo_url: string | null }[]>([]);

  const { data: businesses, isLoading } = useQuery({
    queryKey: ["family-businesses-page"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("family_businesses")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: allOwners } = useQuery({
    queryKey: ["business-owners-all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("business_owners")
        .select("business_id, member_id, family_members(id, name, photo_url)");
      if (error) throw error;
      return data as any[];
    },
  });

  const handleOpenDetail = async (biz: Business) => {
    setSelected(biz);
    const owners = (allOwners || [])
      .filter((o: any) => o.business_id === biz.id && o.family_members)
      .map((o: any) => o.family_members);
    setSelectedOwners(owners);
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-4">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="font-amiri text-4xl md:text-5xl font-bold gold-text mb-3">شركات العائلة</h1>
          <p className="text-muted-foreground text-lg">دليل شركات ومؤسسات عائلة أبوليلة</p>
        </motion.div>

        {isLoading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !businesses?.length ? (
          <p className="text-center text-muted-foreground py-20">لا توجد شركات مضافة حالياً</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {businesses.map((biz, i) => (
              <motion.div
                key={biz.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
              >
                <Card className="glass-card border-primary/10 hover:border-primary/30 transition-all h-full rounded-2xl group">
                  <CardContent className="p-6 flex flex-col items-center text-center gap-4">
                    <div className="w-28 h-28 rounded-full overflow-hidden bg-secondary/30 flex items-center justify-center border-2 border-primary/30 group-hover:border-primary/60 transition-all shadow-lg ring-2 ring-primary/10 ring-offset-2 ring-offset-background">
                      <img
                        src={biz.logo_url || defaultBusiness}
                        alt={biz.name_ar}
                        className={`w-full h-full ${biz.logo_url ? 'object-cover' : 'object-contain p-2'}`}
                        loading="lazy"
                      />
                    </div>
                    <div>
                      <h2 className="font-amiri text-lg font-bold text-primary">{biz.name_ar}</h2>
                      {biz.name_en && <p className="text-sm text-muted-foreground mt-0.5">{biz.name_en}</p>}
                    </div>
                    {/* Social icons row */}
                    <div className="flex flex-wrap gap-2 justify-center">
                      <SocialIcon type="website" url={biz.website} />
                      <SocialIcon type="phone" url={biz.phone} />
                      <SocialIcon type="email" url={biz.email} />
                      <SocialIcon type="whatsapp" url={biz.whatsapp} />
                      <SocialIcon type="instagram" url={biz.instagram} />
                      <SocialIcon type="facebook" url={biz.facebook} />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-auto rounded-xl border-primary/20 text-primary hover:bg-primary/10"
                      onClick={() => handleOpenDetail(biz)}
                    >
                      تفاصيل أكثر
                      <ArrowLeft className="h-3.5 w-3.5 mr-1" />
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <BusinessDetail biz={selected} owners={selectedOwners} open={!!selected} onClose={() => setSelected(null)} />
    </div>
  );
};

export default Businesses;
